import { generatePath as routerGenPath } from "react-router";

import { useUiStore } from "@stores/uiStore";

import type { LoaderFunctionArgs } from "react-router";

import type {
  IExtendedRouteObject,
  IExtractParams,
  IPathParam,
  IPaths,
  IRouteMetadataObj,
} from "./types";

export const extractSearchFromUrl = (request: Request) => {
  return new URL(request.url)?.searchParams;
};

export const fromHandling = {
  fromParmaName: "from",

  /**
   * return `from` param to set in the url (only for make-appointment page)
   * @param request
   * @returns
   */
  createFromParam(request: Request) {
    const params = new URLSearchParams();

    params.set(this.fromParmaName, new URL(request.url).pathname);
    return params.toString();
  },

  /**
   * read and return 'from' parma from the url
   * @param request
   * @returns
   */
  getFromParmaValue(request: Request) {
    const params = new URL(request.url).searchParams;

    return params.get(this.fromParmaName);
  },
};

export function generatePath<T extends string>(
  path: T,
  params: IExtractParams<T> extends undefined
    ? null | undefined
    : IExtractParams<T>,
  search?: string | string[][] | Record<string, string> | URLSearchParams,
) {
  return generate(path, params, search);
}

function generate<T>(
  path: T,
  params?: IExtractParams<T> | null,
  search?: string | string[][] | Record<string, string> | URLSearchParams,
) {
  const searchParams = `?${new URLSearchParams(search).toString()}`;
  const _params = params ?? {};

  let url = routerGenPath(`${path}`, _params);

  url += search ? searchParams : "";
  return url;
}

/**
 * Creates path functions for the given routes configuration.
 * Route object that must be readonly otherwise route params will not be extracted properly
 * @template TRoutes The routes configuration type.
 * @template TRoutesParam The route parameters configuration type.
 * @param routes The routes configuration.
 * @returns The paths configuration.
 *
 * @example
 *
 * const routes = {
 *   list: '/list',
 *   view: '/view/:id',
 * } as const;
 *
 * type Params = IExtractRouteParamMap<typeof routes>;
 *
 * const paths = createPaths<typeof routes, Params>(routes);
 *  Result:
 *  {
 *    list: () => string;
 *    view: (params: { id: string }) => string;
 *  }
 */
export function createPaths<TRoutes, TRoutesParam extends IPathParam<TRoutes>>(
  routes: Record<keyof TRoutes, string>,
): IPaths<TRoutes, TRoutesParam> {
  const pathCreators: Partial<IPaths<TRoutes, TRoutesParam>> = {};

  for (const key in routes) {
    const route = routes[key];

    pathCreators[key] = route.includes(":")
      ? (((params: Parameters<typeof routerGenPath<typeof key>>[1]) =>
          routerGenPath(
            route as unknown as Extract<keyof TRoutes, string>,
            params,
          )) as IPaths<TRoutes, TRoutesParam>[typeof key])
      : ((() => routerGenPath(route)) as IPaths<
          TRoutes,
          TRoutesParam
        >[typeof key]);
  }

  return pathCreators as IPaths<TRoutes, TRoutesParam>;
}

export function createRoute<TRoute>(
  routeObj: Record<keyof TRoute, string>,
  routeObjMetadata: IRouteMetadataObj<TRoute>,
  customLoader?: ({ request }: LoaderFunctionArgs) => Response | null,
): IExtendedRouteObject[] {
  const routes: IExtendedRouteObject[] = [];

  for (const key in routeObj) {
    if (Object.prototype.hasOwnProperty.call(routeObj, key)) {
      const element = routeObj[key];
      const routeMetadata = routeObjMetadata[key];
      const route: IExtendedRouteObject = {
        route: {
          path: element,
          lazy: routeMetadata.lazy,
          loader: ({ request, ...rest }) => {
            return customLoader ? customLoader({ request, ...rest }) : {};
          },
          handle: {
            onLoaded: (request: Request) => {
              useUiStore
                .getState()
                .setPage(
                  request,
                  routeMetadata.title,
                  routeMetadata.description,
                  routeMetadata.mode,
                  routeMetadata.type,
                  routeMetadata.icon,
                  routeMetadata.breadcrumbs,
                  routeMetadata.pageState,
                );
            },
          },
        },
        permissions: routeMetadata.permission,
      };

      routes.push(route);
    }
  }
  return routes;
}
